import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ArrowsRightLeftIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ClockIcon,
  MapPinIcon,
  InformationCircleIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  UserIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  HandThumbUpIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  TruckIcon as TruckIconSolid,
  ArrowsRightLeftIcon as ArrowsRightLeftIconSolid,
  ClockIcon as ClockIconSolid,
  ClipboardDocumentCheckIcon as ClipboardCheckSolid
} from '@heroicons/react/24/solid';
import { getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// استيراد APIs (سنحتاج لإنشاء admin exchange APIs)
import { adminAPI } from '../../config/api';

const ExchangeManagement = () => {
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'completed'
  const [shippingForm, setShippingForm] = useState({
    open: false,
    exchangeId: null,
    direction: 'aToB',
    carrier: '',
    trackingNumber: '',
    estimatedDelivery: ''
  });
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadExchanges();
  }, [filters, activeTab]);

  const loadExchanges = async () => {
    try {
      setLoading(true);
      
      // تحديد الحالات المطلوبة حسب التبويب
      let statusFilter;
      if (activeTab === 'active') {
        // الطلبات غير المكتملة - نمرر حالة محددة أو نتركها فارغة لجلب الكل ثم نفلتر
        if (filters.status !== 'all') {
          statusFilter = filters.status;
        }
        // إذا كان 'all' سنجلب الكل ونفلتر في الفرونت اند
      } else if (activeTab === 'completed') {
        // الطلبات المكتملة فقط
        statusFilter = 'completed';
      }

      const params = {
        status: statusFilter,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      // استخدام API الأدمن الجديد
      const response = await adminAPI.getExchangeTransactions(params);
      // الالتزام بشكل استجابة الباك اند المعياري { success, message, data: { transactions, pagination } }
      let exchangesData = response.data?.data?.transactions || [];
      
      // فلترة البيانات في الفرونت اند للتبويب النشط
      if (activeTab === 'active' && filters.status === 'all') {
        // استثناء الطلبات المكتملة فقط
        exchangesData = exchangesData.filter(exchange => exchange.status !== 'completed');
      }
      
      console.log('Loaded admin exchanges:', exchangesData);
      setExchanges(Array.isArray(exchangesData) ? exchangesData : []);
    } catch (error) {
      console.error('Error loading exchanges:', error);
      setExchanges([]); // تعيين array فارغ في حالة الخطأ
      // لا نعرض toast error إذا كانت المشكلة أن API غير موجود
      if (error.message && !error.message.includes('404')) {
        toast.error('فشل في تحميل معاملات التبادل');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (exchangeId) => {
    try {
      const response = await adminAPI.getExchangeTransaction?.(exchangeId);
      if (response && response.data) {
        setSelectedExchange(response.data.data || response.data);
        setShowDetailsModal(true);
      } else {
        toast.error('لا توجد بيانات للتبادل');
      }
    } catch (error) {
      console.error('Error loading exchange details:', error);
      toast.error('فشل في تحميل تفاصيل التبادل');
    }
  };

  const handleUpdateShipping = async (exchangeId, direction, status, trackingNumber, carrier, estimatedDelivery) => {
    try {
      await adminAPI.updateExchangeShipping?.(exchangeId, {
        direction,
        status,
        trackingNumber,
        carrier,
        estimatedDelivery
      });
      
      toast.success('تم تحديث حالة الشحن');
      loadExchanges();
      if (selectedExchange?._id === exchangeId) {
        handleViewDetails(exchangeId);
      }
    } catch (error) {
      toast.error('فشل في تحديث حالة الشحن');
    }
  };

  const handleConfirmPickup = (exchangeId, direction) => {
    if (!window.confirm('تأكيد استلام الشحنة من البائع؟')) return;
    handleUpdateShipping(exchangeId, direction, 'picked_up');
  };

  const handleOpenShippingForm = (exchangeId, direction) => {
    setShippingForm({
      open: true,
      exchangeId,
      direction,
      carrier: '',
      trackingNumber: '',
      estimatedDelivery: ''
    });
  };

  const handleSubmitShippingForm = async (e) => {
    e.preventDefault();
    const { exchangeId, direction, carrier, trackingNumber, estimatedDelivery } = shippingForm;
    if (!carrier || !trackingNumber) {
      toast.error('الرجاء إدخال شركة الشحن ورقم التتبع');
      return;
    }
    await handleUpdateShipping(exchangeId, direction, 'in_transit', trackingNumber, carrier, estimatedDelivery);
    setShippingForm(prev => ({ ...prev, open: false }));
  };

  const handleConfirmDelivered = (exchangeId, direction) => {
    if (!window.confirm('تأكيد تسليم الشحنة؟')) return;
    handleUpdateShipping(exchangeId, direction, 'delivered');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { 
        icon: CheckCircleIcon, 
        text: 'مؤكد', 
        color: 'bg-green-100 text-green-800' 
      },
      shipping_a_to_b: { 
        icon: TruckIcon, 
        text: 'شحن A→B', 
        color: 'bg-blue-100 text-blue-800' 
      },
      shipping_b_to_a: { 
        icon: TruckIcon, 
        text: 'شحن B→A', 
        color: 'bg-blue-100 text-blue-800' 
      },
      both_shipping: { 
        icon: TruckIcon, 
        text: 'شحن مزدوج', 
        color: 'bg-indigo-100 text-indigo-800' 
      },
      completed: { 
        icon: CheckCircleIconSolid, 
        text: 'مكتمل', 
        color: 'bg-emerald-100 text-emerald-800' 
      }
    };
    
    const config = statusConfig[status] || statusConfig.confirmed;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-4 h-4 ml-1" />
        {config.text}
      </span>
    );
  };

  const getShippingStatusBadge = (status) => {
    const statusConfig = {
      pending: { text: 'في الانتظار', color: 'bg-gray-100 text-gray-800' },
      picked_up: { text: 'تم الاستلام', color: 'bg-blue-100 text-blue-800' },
      in_transit: { text: 'في الطريق', color: 'bg-yellow-100 text-yellow-800' },
      delivered: { text: 'تم التسليم', color: 'bg-green-100 text-green-800' },
      failed: { text: 'فشل', color: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const renderShippingCard = (exchange, direction, title) => {
    const shipping = exchange.shipping?.[direction];
    if (!shipping) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">{title}</h4>
          {getShippingStatusBadge(shipping.status)}
        </div>

        <div className="space-y-2 text-sm text-gray-600 mb-4">
          {shipping.trackingNumber && (
            <p><span className="font-medium">رقم التتبع:</span> {shipping.trackingNumber}</p>
          )}
          {shipping.estimatedDelivery && (
            <p><span className="font-medium">التسليم المتوقع:</span> {new Date(shipping.estimatedDelivery).toLocaleDateString('ar-SA')}</p>
          )}
          {shipping.pickedUpAt && (
            <p><span className="font-medium">تاريخ الاستلام:</span> {new Date(shipping.pickedUpAt).toLocaleDateString('ar-SA')}</p>
          )}
          {shipping.deliveredAt && (
            <p><span className="font-medium">تاريخ التسليم:</span> {new Date(shipping.deliveredAt).toLocaleDateString('ar-SA')}</p>
          )}
        </div>

        <div className="flex gap-2">
          {shipping.status === 'pending' && (
            <button
              onClick={() => handleConfirmPickup(exchange._id, direction)}
              className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-200 transition-colors text-sm"
            >
              تأكيد استلام من البائع
            </button>
          )}
          {shipping.status === 'picked_up' && (
            <button
              onClick={() => handleOpenShippingForm(exchange._id, direction)}
              className="flex-1 bg-indigo-100 text-indigo-700 py-2 px-3 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
            >
              إضافة معلومات الشحن
            </button>
          )}
          {shipping.status === 'in_transit' && (
            <button
              onClick={() => handleConfirmDelivered(exchange._id, direction)}
              className="flex-1 bg-green-100 text-green-700 py-2 px-3 rounded-lg hover:bg-green-200 transition-colors text-sm"
            >
              تأكيد التسليم
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderExchangeCard = (exchange) => (
    <motion.div
      key={exchange._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
    >
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ArrowsRightLeftIconSolid className="h-6 w-6 text-white ml-2" />
            <h3 className="text-lg font-semibold text-white">معاملة تبادل #{exchange.transactionNumber}</h3>
          </div>
          {getStatusBadge(exchange.status)}
        </div>
      </div>
      <div className="p-6">

        {/* معلومات الأطراف */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <BuildingStorefrontIcon className="w-5 h-5 ml-2 text-blue-600" />
              المختبر الأول (A)
            </h4>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-sm">{exchange.parties?.labA?.lab?.labName}</p>
                <p className="text-xs text-gray-600">المنتج: {exchange.parties?.labA?.product?.name || exchange.parties?.labA?.customProductName || 'عرض مخصص'}</p>
              </div>
              {exchange.parties?.labA?.lab?.address && (
                <div className="flex items-start">
                  <MapPinIcon className="w-4 h-4 text-gray-400 ml-1 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600">{exchange.parties.labA.lab.address}</p>
                </div>
              )}
              {exchange.parties?.labA?.user?.phone && (
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4 text-gray-400 ml-1" />
                  <p className="text-xs text-gray-600">{exchange.parties.labA.user.phone}</p>
                </div>
              )}
              {exchange.parties?.labA?.user?.name && (
                <div className="flex items-center">
                  <UserIcon className="w-4 h-4 text-gray-400 ml-1" />
                  <p className="text-xs text-gray-600">{exchange.parties.labA.user.name}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <BuildingStorefrontIcon className="w-5 h-5 ml-2 text-green-600" />
              المختبر الثاني (B)
            </h4>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-sm">{exchange.parties?.labB?.lab?.labName}</p>
                <p className="text-xs text-gray-600">المنتج: {exchange.parties?.labB?.product?.name}</p>
              </div>
              {exchange.parties?.labB?.lab?.address && (
                <div className="flex items-start">
                  <MapPinIcon className="w-4 h-4 text-gray-400 ml-1 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600">{exchange.parties.labB.lab.address}</p>
                </div>
              )}
              {exchange.parties?.labB?.user?.phone && (
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4 text-gray-400 ml-1" />
                  <p className="text-xs text-gray-600">{exchange.parties.labB.user.phone}</p>
                </div>
              )}
              {exchange.parties?.labB?.user?.name && (
                <div className="flex items-center">
                  <UserIcon className="w-4 h-4 text-gray-400 ml-1" />
                  <p className="text-xs text-gray-600">{exchange.parties.labB.user.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* معلومات مالية */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">إجمالي القيمة:</p>
              <p className="font-medium">{exchange.financial?.totalValue?.toLocaleString()} ريال</p>
            </div>
            <div>
              <p className="text-gray-600">الرسوم:</p>
              <p className="font-medium text-blue-600">{exchange.financial?.feeAmount?.toLocaleString()} ريال</p>
            </div>
          </div>
        </div>

        {/* حالة الشحن المزدوج - متاحة دائماً لبدء الشحن من حالة confirmed */}
        {exchange.shipping && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {renderShippingCard(
              exchange, 
              'aToB', 
              `شحن: ${exchange.parties?.labA?.lab?.labName} → ${exchange.parties?.labB?.lab?.labName}`
            )}
            {renderShippingCard(
              exchange, 
              'bToA', 
              `شحن: ${exchange.parties?.labB?.lab?.labName} → ${exchange.parties?.labA?.lab?.labName}`
            )}
          </div>
        )}

        {/* الأزرار */}
        <div className="flex gap-3">
          <button
            onClick={() => handleViewDetails(exchange._id)}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <EyeIcon className="w-4 h-4 ml-1" />
            عرض التفاصيل
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 ml-1" />
              {new Date(exchange.createdAt).toLocaleDateString('ar-SA')}
            </div>
            <div className="flex items-center">
              <CurrencyDollarIcon className="w-4 h-4 ml-1" />
              {exchange.financial?.totalValue?.toLocaleString()} ريال
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ArrowsRightLeftIcon className="h-8 w-8 ml-3 text-blue-600" />
                إدارة طلبات التبادل
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                متابعة وإدارة جميع معاملات التبادل والشحنات
              </p>
            </div>
            <button
              onClick={loadExchanges}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowPathIcon className="h-5 w-5 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* التبويبات */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <ClockIcon className="w-5 h-5 ml-2" />
                الطلبات النشطة
              </div>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'completed'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <CheckCircleIconSolid className="w-5 h-5 ml-2" />
                الطلبات المكتملة
              </div>
            </button>
          </nav>
        </div>

        {/* الفلاتر */}
        <div className="p-4">
          <div className="flex flex-wrap gap-4">
            {activeTab === 'active' && (
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">جميع الحالات النشطة</option>
                <option value="confirmed">مؤكد</option>
                <option value="shipping_a_to_b">شحن A→B</option>
                <option value="shipping_b_to_a">شحن B→A</option>
                <option value="both_shipping">شحن مزدوج</option>
              </select>
            )}
            
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters(prev => ({ ...prev, sortBy, sortOrder }));
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="createdAt-desc">الأحدث أولاً</option>
              <option value="createdAt-asc">الأقدم أولاً</option>
              <option value="totalValue-desc">الأعلى قيمة</option>
              <option value="totalValue-asc">الأقل قيمة</option>
            </select>
          </div>
        </div>
      </div>

      {/* قائمة التبادلات */}
      <div className="grid grid-cols-1 gap-6">
        {exchanges.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            {activeTab === 'active' ? (
              <>
                <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد طلبات نشطة</h3>
                <p className="text-gray-500">جميع طلبات التبادل مكتملة أو لا توجد طلبات حالياً</p>
              </>
            ) : (
              <>
                <CheckCircleIconSolid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد طلبات مكتملة</h3>
                <p className="text-gray-500">لم يتم إكمال أي طلبات تبادل بعد</p>
              </>
            )}
          </div>
        ) : (
          exchanges.map(renderExchangeCard)
        )}
      </div>

      {/* نموذج التفاصيل */}
      <AnimatePresence>
        {showDetailsModal && selectedExchange && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center">
                      <ArrowsRightLeftIconSolid className="h-6 w-6 ml-2" />
                      تفاصيل معاملة التبادل
                    </h2>
                    <p className="text-blue-100 mt-1">#{selectedExchange.transactionNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">

                {/* الجدول الزمني */}
                {selectedExchange.timeline && selectedExchange.timeline.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 ml-2 text-blue-600" />
                      الجدول الزمني
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {selectedExchange.timeline.map((event, index) => (
                          <div key={index} className="flex items-center justify-between p-2">
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                              <span className="text-sm font-medium">{event.description}</span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(event.timestamp).toLocaleString('ar-SA')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* معلومات العناوين */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* عنوان الاستلام من المختبر A */}
                  {selectedExchange.parties?.labA?.lab && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <MapPinIcon className="h-5 w-5 ml-2 text-emerald-600" />
                        عنوان الاستلام من المختبر A
                      </h3>
                      <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-1 gap-3">
                          <div className="flex items-center">
                            <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span className="font-medium">{selectedExchange.parties.labA.lab.labName}</span>
                          </div>
                          {selectedExchange.parties?.labA?.user?.phone && (
                            <div className="flex items-center">
                              <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                              <span>{selectedExchange.parties.labA.user.phone}</span>
                            </div>
                          )}
                          {selectedExchange.parties?.labA?.user?.name && (
                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 ml-2 text-gray-400" />
                              <span>{selectedExchange.parties.labA.user.name}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-1">
                          <span className="text-gray-600">العنوان:</span>
                          <p className="text-gray-800 mt-1">{selectedExchange.parties.labA.lab.address || 'غير محدد'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* عنوان التسليم للمختبر B */}
                  {selectedExchange.parties?.labB?.lab && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <MapPinIcon className="h-5 w-5 ml-2 text-amber-600" />
                        عنوان التسليم للمختبر B
                      </h3>
                      <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-1 gap-3">
                          <div className="flex items-center">
                            <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span className="font-medium">{selectedExchange.parties.labB.lab.labName}</span>
                          </div>
                          {selectedExchange.parties?.labB?.user?.phone && (
                            <div className="flex items-center">
                              <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                              <span>{selectedExchange.parties.labB.user.phone}</span>
                            </div>
                          )}
                          {selectedExchange.parties?.labB?.user?.name && (
                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 ml-2 text-gray-400" />
                              <span>{selectedExchange.parties.labB.user.name}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-1">
                          <span className="text-gray-600">العنوان:</span>
                          <p className="text-gray-800 mt-1">{selectedExchange.parties.labB.lab.address || 'غير محدد'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* دليل المنتجات للشحن */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 ml-2 text-indigo-600" />
                    دليل المنتجات للشحن
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">الشحنة الأولى (A→B)</h4>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">المنتج المُرسل:</span> {selectedExchange.parties?.labA?.product?.name || selectedExchange.parties?.labA?.customProductName || 'عرض مخصص'}</p>
                        <p><span className="font-medium">الكمية:</span> {selectedExchange.parties?.labA?.quantity}</p>
                        <p><span className="font-medium">من:</span> {selectedExchange.parties?.labA?.lab?.labName}</p>
                        <p><span className="font-medium">إلى:</span> {selectedExchange.parties?.labB?.lab?.labName}</p>
                      </div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-medium text-green-900 mb-2">الشحنة الثانية (B→A)</h4>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">المنتج المُرسل:</span> {selectedExchange.parties?.labB?.product?.name}</p>
                        <p><span className="font-medium">الكمية:</span> {selectedExchange.parties?.labB?.quantity}</p>
                        <p><span className="font-medium">من:</span> {selectedExchange.parties?.labB?.lab?.labName}</p>
                        <p><span className="font-medium">إلى:</span> {selectedExchange.parties?.labA?.lab?.labName}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* تفاصيل الشحن */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {renderShippingCard(
                    selectedExchange, 
                    'aToB', 
                    'الشحنة الأولى (A→B)'
                  )}
                  {renderShippingCard(
                    selectedExchange, 
                    'bToA', 
                    'الشحنة الثانية (B→A)'
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* مودال إضافة معلومات الشحن */}
      <AnimatePresence>
        {shippingForm.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShippingForm(prev => ({ ...prev, open: false }))}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSubmitShippingForm} className="p-6 space-y-4">
                <h3 className="text-lg font-semibold">إضافة معلومات الشحن</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">شركة الشحن</label>
                  <input
                    type="text"
                    value={shippingForm.carrier}
                    onChange={(e) => setShippingForm(prev => ({ ...prev, carrier: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="اسم شركة الشحن"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">رقم التتبع</label>
                  <input
                    type="text"
                    value={shippingForm.trackingNumber}
                    onChange={(e) => setShippingForm(prev => ({ ...prev, trackingNumber: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="رقم التتبع"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">تاريخ التسليم المتوقع (اختياري)</label>
                  <input
                    type="date"
                    value={shippingForm.estimatedDelivery}
                    onChange={(e) => setShippingForm(prev => ({ ...prev, estimatedDelivery: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShippingForm(prev => ({ ...prev, open: false }))}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white py-2 rounded-lg hover:opacity-95 transition-all"
                  >
                    حفظ وإرسال
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExchangeManagement;
